import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/components/providers/auth-provider'
import { LoadingSpinner } from '@/components/shared/loading-spinner'
import type { SubscriptionTier } from '@/types'

interface ProtectedRouteProps {
    children: React.ReactNode
    requiredTier?: SubscriptionTier | SubscriptionTier[]
    requiredRole?: string // Keep for backward compatibility
    requireEnterprise?: boolean

    redirectTo?: string
}

export function ProtectedRoute({
    children,
    requiredTier,
    requiredRole, // Legacy prop
    redirectTo = '/auth/login'
}: ProtectedRouteProps) {
    const { isAuthenticated, isLoading, user } = useAuth()
    const location = useLocation()

    // Show loading spinner while checking authentication
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <LoadingSpinner size="lg" />
            </div>
        )
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        return (
            <Navigate
                to={redirectTo}
                state={{ from: location.pathname }}
                replace
            />
        )
    }

    // Handle legacy requiredRole prop (map to tier)
    let tierRequirement: SubscriptionTier | SubscriptionTier[] | undefined = requiredTier
    if (requiredRole && !requiredTier) {
        // Map legacy role to tier
        if (requiredRole === 'pro') {
            tierRequirement = ['pro', 'enterprise']
        } else if (requiredRole === 'enterprise') {
            tierRequirement = ['enterprise']
        }
    }

    // Check tier requirements if specified
    if (tierRequirement && user) {
        const userTier = user.subscription_tier || 'free'
        const requiredTiers = Array.isArray(tierRequirement) ? tierRequirement : [tierRequirement]
        
        if (!requiredTiers.includes(userTier)) {
            const tierNames: Record<SubscriptionTier, string> = {
                free: 'Free',
                starter: 'Starter',
                pro: 'Pro',
                team: 'Team',
                enterprise: 'Enterprise'
            }

            const requiredTierName = requiredTiers.length === 1 
                ? tierNames[requiredTiers[0]]
                : requiredTiers.map(t => tierNames[t]).join(' or ')

            return (
                <div className="min-h-screen flex items-center justify-center bg-background p-4">
                    <div className="max-w-md w-full text-center">
                        <div className="mb-6">
                            <div className="mx-auto w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center mb-4">
                                <svg
                                    className="w-6 h-6 text-warning"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 0h12a2 2 0 002-2v-4a2 2 0 00-2-2H6a2 2 0 00-2 2v4a2 2 0 002 2z"
                                    />
                                </svg>
                            </div>
                            <h1 className="text-2xl font-bold text-foreground mb-2">
                                Access Restricted
                            </h1>
                            <p className="text-muted-foreground mb-6">
                                You need a {requiredTierName} subscription to access this feature.
                            </p>
                        </div>

                        <div className="space-y-3">
                            <button
                                onClick={() => window.location.href = '/billing'}
                                className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                            >
                                Upgrade Subscription
                            </button>
                            <button
                                onClick={() => window.history.back()}
                                className="w-full px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
                            >
                                Go Back
                            </button>
                        </div>
                    </div>
                </div>
            )
        }
    }

    // Render protected content
    return <>{children}</>
}